import styles from "./index.module.scss";
import { use100vh } from "../../hooks/use100vh";
import {
  ArrowRight,
  BarChart,
  Box,
  Database,
  Envelope,
  FileText,
  Gem,
  HandThumbsUp,
  Palette,
  PatchCheck,
  People,
  Phone,
} from "react-bootstrap-icons";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import emailjs from "@emailjs/browser";
import { useCallback, useEffect, useMemo } from "react";
import Footer from "../../components/Footer/Footer";
import catlogicLogo from "../../assets/logo_white.svg";
import SiteNav from "../../components/SiteNav/SiteNav";
import Seo, { SITE_URL } from "../../components/Seo/Seo";
import { HOME_COPY } from "../../content/localizedPageCopy";
import { useLocale } from "../../contexts/LocaleContext";
import { HomeSection, parseHomeSectionTag, scrollToHomeSection, scrollToHomeSectionWhenReady } from "../../utils/homeSectionNavigation";
import { createHeroParticles } from "../../utils/heroParticles";

const WHAT_WE_DO_ICONS = [<Gem />, <Box />, <People />, <PatchCheck />];

const SERVICE_ICONS = [<FileText />, <BarChart />, <Palette />, <HandThumbsUp />, <Database />, <Phone />];

type ProductCardProps = {
  accent: string;
  accentTint: string;
  kicker: string;
  domain: string;
  name: string;
  tagline: string;
  title: string;
  body: string;
  highlights: { label: string; title: string; body: string }[];
  pills: string[];
  href: string;
  domainHref?: string;
  ctaLabel: string;
};

function ProductCard({ accent, accentTint, kicker, domain, name, tagline, title, body, highlights, pills, href, domainHref, ctaLabel }: ProductCardProps) {
  const isExternal = /^https?:\/\//.test(href);
  const resolvedDomainHref = domainHref ?? href;
  const isDomainExternal = /^https?:\/\//.test(resolvedDomainHref);

  return (
    <article className={styles.productCard} style={{ "--accent": accent, "--accent-tint": accentTint } as React.CSSProperties}>
      <div className={styles.productCardHead}>
        <span className={styles.productCardKicker}>{kicker}</span>
        {isDomainExternal ? (
          <a className={styles.productDomain} href={resolvedDomainHref} target="_blank" rel="noreferrer">
            {domain}
          </a>
        ) : (
          <Link className={styles.productDomain} to={resolvedDomainHref}>
            {domain}
          </Link>
        )}
      </div>

      <div className={styles.productWordmark}>
        <strong>{name}</strong>
        <span>{tagline}</span>
      </div>

      <h3>{title}</h3>
      <p>{body}</p>

      <ul className={styles.productPoints}>
        {highlights.map((item) => (
          <li key={item.title}>
            <span className={styles.productPointDot} />
            <div>
              <strong>{item.title}</strong>
              <p>{item.body}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className={styles.productCardFooter}>
        <div className={styles.productPills}>
          {pills.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>

        {isExternal ? (
          <a className={styles.productCta} href={href} target="_blank" rel="noreferrer">
            {ctaLabel} <ArrowRight />
          </a>
        ) : (
          <Link className={styles.productCta} to={href}>
            {ctaLabel} <ArrowRight />
          </Link>
        )}
      </div>
    </article>
  );
}

export default function HomeView() {
  const size = use100vh();
  const location = useLocation();
  const { locale } = useLocale();
  const windowSize = useWindowDimensions();
  const copy = HOME_COPY[locale];
  const heroParticles = useMemo(() => createHeroParticles(), []);

  useEffect(() => emailjs.init("6i9htOJiem6rEGLqo"), []);

  const isMobile = windowSize.width < 500;

  const handleSectionScroll = useCallback(
    (section: HomeSection) => {
      scrollToHomeSection(section, isMobile);
    },
    [isMobile],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = parseHomeSectionTag(params.get("tagId"));

    if (!section) return;

    return scrollToHomeSectionWhenReady({
      section,
      isMobile,
      onComplete: () => {
        params.delete("tagId");

        const nextQuery = params.toString();
        const nextUrl = nextQuery ? `${location.pathname}?${nextQuery}` : location.pathname;

        window.history.replaceState(null, "", nextUrl);
      },
    });
  }, [isMobile, location.pathname, location.search]);

  const structuredData = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: copy.pageTitle,
      description: copy.metaDescription,
      url: `${SITE_URL}/`,
      inLanguage: locale,
      isPartOf: {
        "@type": "WebSite",
        name: "CatLogic",
        url: SITE_URL,
      },
      about: {
        "@type": "Organization",
        name: "CatLogic",
        url: SITE_URL,
      },
    }),
    [copy, locale]
  );

  return (
    <main className={styles.main}>
      <Seo
        title={copy.pageTitle}
        description={copy.metaDescription}
        keywords={copy.metaKeywords}
        path="/"
        structuredData={structuredData}
      />

      <SiteNav solidThreshold={size.initialHeight ?? 720} />

      <section className={styles.hero}>
        <div className={styles.heroParticles} aria-hidden="true">
          {heroParticles.map((particle, index) => (
            <span
              key={index}
              className={styles.particle}
              style={
                {
                  width: particle.size,
                  height: particle.size,
                  top: particle.top,
                  left: particle.left,
                  background: `radial-gradient(circle, ${particle.color}, transparent 70%)`,
                  animationDuration: particle.duration,
                  animationDelay: particle.delay,
                  "--particle-scale-mid": particle.scaleMid,
                  "--particle-scale-low": particle.scaleLow,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <div className={styles.container}>
          <div className={styles.heroCenter}>
            <img className={styles.heroLogo} src={catlogicLogo} alt="CatLogic" />
            <span className={styles.heroCaption}>{copy.heroBadge}</span>

            <h1 className={styles.heroTitle}>
              {copy.heroTitle} <span>{copy.heroTitleAccent}</span>
            </h1>
            <p className={styles.heroDescription}>{copy.heroDescription}</p>

            <div className={styles.heroActions}>
              <button type="button" className={styles.primaryButton} onClick={() => handleSectionScroll("contacts")}>
                {copy.primaryCta}
              </button>
              <button type="button" className={styles.secondaryButton} onClick={() => handleSectionScroll("services")}>
                {copy.secondaryCta} <ArrowRight />
              </button>
            </div>
          </div>

          <div className={styles.heroMetrics}>
            {copy.heroMetrics.map((item) => (
              <div key={item.title} className={styles.metric}>
                <strong>{item.title}</strong>
                <span>{item.body}</span>
              </div>
            ))}
          </div>

          <div className={styles.heroProcess}>
            <div className={styles.processIntro}>
              <span className={styles.panelTag}>{copy.panelTag}</span>
              <h2>{copy.panelTitle}</h2>
              <p>{copy.panelBody}</p>
            </div>

            <div className={styles.processSteps}>
              {copy.deliveryFlow.map((step, index) => (
                <div key={step} className={styles.processStep}>
                  <span className={styles.processIndex}>{`0${index + 1}`}</span>
                  <p>{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className={styles.surface}>
        <section id="What_we_do" className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.sectionIntro}>
              <div>
                <span className={styles.kicker}>{copy.missionKicker}</span>
                <h2>{copy.missionTitle}</h2>
              </div>
              <p>{copy.missionBody}</p>
            </div>

            <div className={styles.grid}>
              {copy.whatWeDo.map((item, index) => (
                <article key={item.title} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div className={styles.icon}>{WHAT_WE_DO_ICONS[index]}</div>
                    <span className={styles.cardIndex}>{`0${index + 1}`}</span>
                  </div>
                  <h6>{item.title}</h6>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="Future" className={styles.darkBand}>
          <div className={styles.container}>
            <div className={styles.futureLayout}>
              <div className={styles.futureHeading}>
                <span className={styles.kicker}>{copy.futureKicker}</span>
                <h3>{copy.futureTitle}</h3>
              </div>

              <div className={styles.futureContent}>
                <p>{copy.futureBody}</p>

                <div className={styles.futureNotes}>
                  {copy.heroMetrics.map((item) => (
                    <div key={item.title} className={styles.futureNote}>
                      <strong>{item.title}</strong>
                      <span>{item.body}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="Services" className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.sectionIntro}>
              <div>
                <span className={styles.kicker}>{copy.servicesKicker}</span>
                <h2>{copy.servicesTitle}</h2>
              </div>
              <p>{copy.servicesBody}</p>
            </div>

            <div className={styles.serviceGrid}>
              {copy.services.map((item, index) => (
                <article key={item.title} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div className={styles.icon}>{SERVICE_ICONS[index]}</div>
                  </div>
                  <h6>{item.title}</h6>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="stafflow" className={styles.darkBand}>
          <div className={styles.container}>
            <span className={styles.kicker}>{copy.productsKicker}</span>

            <div className={styles.productsGrid}>
              <ProductCard
                accent="#6cb9f0"
                accentTint="rgba(108, 185, 240, 0.14)"
                kicker={copy.productKicker}
                domain="stafflow.by"
                name="Stafflow"
                tagline="by CatLogic"
                title={copy.productTitle}
                body={copy.productBody}
                highlights={copy.productHighlights}
                pills={copy.productPills}
                href="https://www.stafflow.by/"
                ctaLabel={copy.learnMore}
              />

              <ProductCard
                accent="#b9a3ff"
                accentTint="rgba(185, 163, 255, 0.14)"
                kicker={copy.hireKicker}
                domain="stafflow.by/hire"
                name="Hire"
                tagline="by Stafflow"
                title={copy.hireTitle}
                body={copy.hireBody}
                highlights={copy.hireHighlights}
                pills={copy.hirePills}
                href="https://www.stafflow.by/hire"
                ctaLabel={copy.learnMore}
              />

              <ProductCard
                accent="#f0c66c"
                accentTint="rgba(240, 198, 108, 0.14)"
                kicker={copy.aiKicker}
                domain="karpuk.by"
                name="AI Rollout"
                tagline="by CatLogic"
                title={copy.aiTitle}
                body={copy.aiBody}
                highlights={copy.aiHighlights}
                pills={copy.aiPills}
                href="/ai"
                domainHref="https://karpuk.by/"
                ctaLabel={copy.learnMore}
              />
            </div>
          </div>
        </section>

        <section id="Contacts" className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.contactPanel}>
              <div className={styles.contactLayout}>
                <div className={styles.contactCopy}>
                  <span className={styles.kicker}>{copy.contactsKicker}</span>
                  <h2>{copy.contactsTitle}</h2>
                  <p>{copy.contactsBody}</p>

                  <a className={styles.contactLink} href="mailto:info@catlogic.tech?subject=CatLogic%20inquiry">
                    <span className={styles.contactLinkIcon}>
                      <Envelope />
                    </span>
                    info@catlogic.tech
                  </a>
                </div>

                <div className={styles.formShell}>
                  <span className={styles.formTitle}>{copy.formTitle}</span>
                  <BitrixForm />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </main>
  );
}

const BitrixForm: React.FC = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn-ru.bitrix24.by/b28691664/crm/form/loader_6.js?" + ((Date.now() / 180000) | 0);
    script.async = true;
    script.setAttribute("data-b24-form", "inline/6/457hyu");
    script.setAttribute("data-skip-moving", "true");

    const scriptContainer = document.getElementById("bitrix-form-container");
    if (scriptContainer) {
      scriptContainer.appendChild(script);
    }

    return () => {
      if (scriptContainer) {
        scriptContainer.removeChild(script);
      }
    };
  }, []);

  return (
    <div className={styles.form}>
      <div id="bitrix-form-container"></div>
    </div>
  );
};
